<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);

const { t } = useI18n();
const route = useRoute();

const searchQuery = ref('');
const routerPage = computed(() => Number(route.query.page ?? 1));

const filteredRecords = computed(() => {
  if (!searchQuery.value) return records.value;
  const query = searchQuery.value.toLowerCase();
  return records.value.filter(log => {
    const logText = generateLogText(log).toLowerCase();
    return logText.includes(query) || log.remote_address?.includes(query);
  });
});

const fetchAuditLogs = page => {
  try {
    store.dispatch('auditlogs/fetch', { page });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { page: page } });
};

const getActionIcon = auditLogItem => {
  const action = auditLogItem.auditable_type?.toLowerCase() || '';
  if (action.includes('user') || action.includes('agent'))
    return 'i-ph-user-bold';
  if (action.includes('conversation')) return 'i-ph-chat-circle-text-bold';
  if (action.includes('inbox')) return 'i-ph-tray-bold';
  if (action.includes('team')) return 'i-ph-users-three-bold';
  if (action.includes('label')) return 'i-ph-tag-bold';
  if (action.includes('automation')) return 'i-ph-lightning-bold';
  if (action.includes('contact')) return 'i-ph-address-book-bold';
  return 'i-ph-gear-bold';
};

const getActionTone = auditLogItem => {
  const action = auditLogItem.action?.toLowerCase() || '';
  if (action.includes('create')) return 'create';
  if (action.includes('update')) return 'update';
  if (action.includes('delete') || action.includes('destroy')) return 'delete';
  return 'other';
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchAuditLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchAuditLogs(newPage);
  }
});
</script>

<template>
  <div class="audit-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="audit-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <div class="flex items-center gap-2">
        <span class="i-ph-scroll-bold size-5" style="color: #6b7280" />
        <h1 class="audit-title">{{ $t('AUDIT_LOGS.HEADER') }}</h1>
      </div>
      <div class="flex items-center gap-4">
        <span class="audit-stat-pill">
          <span class="audit-stat-pill__num">{{ meta.totalEntries || 0 }}</span>
          total events
        </span>
      </div>
    </header>

    <!-- Description + Search bar -->
    <div class="audit-toolbar px-6 py-3">
      <div class="relative flex-1 max-w-sm">
        <span
          class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
          style="color: #9ca3af"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="audit-search reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
          placeholder="Search audit logs..."
        />
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Loading -->
      <div
        v-if="uiFlags.fetchingList"
        class="flex items-center justify-center py-20"
      >
        <div class="audit-spinner" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center py-24"
      >
        <div class="audit-empty-icon">
          <span class="i-ph-scroll-bold size-10" style="color: #9ca3af" />
        </div>
        <h3 class="audit-empty-title">No Audit Logs</h3>
        <p class="audit-empty-desc">{{ $t('AUDIT_LOGS.LIST.404') }}</p>
      </div>

      <!-- Audit Log Timeline -->
      <div v-else class="px-6 py-4">
        <div class="audit-timeline">
          <div
            v-for="(log, index) in filteredRecords"
            :key="log.id"
            class="audit-item"
          >
            <!-- Dot + connector -->
            <div class="audit-item__line">
              <div
                class="audit-dot"
                :class="`audit-dot--${getActionTone(log)}`"
              >
                <span
                  class="audit-dot__icon size-4"
                  :class="getActionIcon(log)"
                />
              </div>
              <div
                v-if="index < filteredRecords.length - 1"
                class="audit-connector"
              />
            </div>

            <!-- Card -->
            <div class="audit-card">
              <p class="audit-card__text">{{ generateLogText(log) }}</p>
              <div class="audit-card__meta">
                <span class="audit-meta-chip">
                  <span class="i-ph-clock-bold size-3" />
                  {{ messageTimestamp(log.created_at, 'MMM dd, yyyy hh:mm a') }}
                </span>
                <span
                  v-if="log.remote_address"
                  class="audit-meta-chip font-mono"
                >
                  <span class="i-ph-globe-bold size-3" />
                  {{ log.remote_address }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="audit-pagination">
          <TableFooter
            :current-page="Number(meta.currentPage)"
            :total-count="meta.totalEntries"
            :page-size="meta.perPage"
            class="!px-0"
            @page-change="onPageChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Forced white theme ── */
.audit-page,
.dark .audit-page {
  background: #ffffff !important;
}

.audit-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.audit-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.audit-stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 12px;
  color: #6b7280;
}
.audit-stat-pill__num {
  font-weight: 700;
  color: #111827;
}

.audit-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 16px;
}

.audit-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.audit-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #ffffff !important;
}
.audit-search::placeholder {
  color: #9ca3af !important;
}

/* Spinner */
.audit-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: audit-spin 0.9s linear infinite;
}
@keyframes audit-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty state */
.audit-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}
.audit-empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px;
}
.audit-empty-desc {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  text-align: center;
}

/* Timeline */
.audit-timeline {
  display: flex;
  flex-direction: column;
}

.audit-item {
  display: flex;
  gap: 16px;
  min-height: 72px;
}

.audit-item__line {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 36px;
  flex-shrink: 0;
}

.audit-dot {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
}

.audit-dot__icon {
  color: #6b7280;
}

/* Color variants for action dots */
.audit-dot--create {
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.audit-dot--create .audit-dot__icon {
  color: #047857;
}
.audit-dot--update {
  background: #eff6ff;
  border-color: #bfdbfe;
}
.audit-dot--update .audit-dot__icon {
  color: #1d4ed8;
}
.audit-dot--delete {
  background: #fef2f2;
  border-color: #fecaca;
}
.audit-dot--delete .audit-dot__icon {
  color: #dc2626;
}
.audit-dot--other {
  background: #fffbeb;
  border-color: #fde68a;
}
.audit-dot--other .audit-dot__icon {
  color: #b45309;
}

.audit-connector {
  width: 1px;
  flex: 1;
  background: #e5e7eb;
  margin: 6px 0;
}

.audit-card {
  flex: 1;
  padding: 12px 14px;
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
}

.audit-card__text {
  font-size: 13px;
  color: #111827;
  line-height: 1.55;
  margin: 0 0 8px;
}

.audit-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.audit-meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
}

.audit-pagination {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

/* Pagination overrides */
:deep(footer) {
  padding: 0 !important;
  background: transparent !important;
}
:deep(.outline-n-weak) {
  outline-color: #d1d5db !important;
}
:deep(.bg-n-slate-9\/10) {
  background: #f9fafb !important;
}
:deep(.bg-n-strong) {
  background: #d1d5db !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(button[class*='bg-n-slate-9']) {
  background: #f9fafb !important;
  color: #374151 !important;
}
:deep(button[class*='bg-n-slate-9']:hover) {
  background: #f3f4f6 !important;
}
</style>
